import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { ArrowLeft, Save, Loader2, User } from "lucide-react";

interface Customer {
  id: string;
  email: string;
}

export default function NewCustomerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    street: "",
    city: "",
    zip: "",
    country: "US",
    birthDate: "",
    passportNumber: "",
    passportExpiry: "",
    nationality: "",
    notes: "",
    newsletterSubscribed: false,
    source: "admin",
  });

  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      api.post<ApiResponse<Customer>>("/api/admin/customers", data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      navigate(`/customers/${data.data.id}`);
    },
    onError: (err: Error) => {
      setError(err.message || t("crm.newCustomer.createFailed"));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.email || !formData.firstName || !formData.lastName) {
      setError(t("crm.newCustomer.fillRequired"));
      return;
    }

    createMutation.mutate(formData);
  };

  return (
    <div className="mx-auto max-w-2xl">
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <button onClick={() => navigate("/customers")} className="btn-ghost btn-sm">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("crm.customers.newCustomer")}
          </h1>
          <p className="text-sm text-gray-500">{t("crm.newCustomer.subtitle")}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-6">
        {error && (
          <div className="bg-error-50 text-error-700 dark:bg-error-900/30 dark:text-error-400 rounded-lg p-4">
            {error}
          </div>
        )}

        {/* Basic info */}
        <div>
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
            <User className="h-5 w-5" />
            {t("crm.newCustomer.basicInfo")}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("crm.fields.firstName")} *
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="input"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("crm.fields.lastName")} *
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="input"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("common.fields.email")} *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("common.fields.phone")}
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input"
                placeholder={t("crm.newCustomer.phonePlaceholder")}
              />
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="border-t pt-6 dark:border-gray-700">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            {t("crm.newCustomer.address")}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("crm.fields.street")}
              </label>
              <input
                type="text"
                value={formData.street}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("crm.fields.city")}
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("crm.fields.postalCode")}
              </label>
              <input
                type="text"
                value={formData.zip}
                onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("crm.fields.country")}
              </label>
              <select
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="input"
              >
                <option value="US">{t("crm.newCustomer.countries.us")}</option>
                <option value="GB">{t("crm.newCustomer.countries.gb")}</option>
                <option value="AU">{t("crm.newCustomer.countries.au")}</option>
                <option value="CA">{t("crm.newCustomer.countries.ca")}</option>
                <option value="VN">{t("crm.newCustomer.countries.vn")}</option>
                <option value="OTHER">{t("crm.newCustomer.countries.other")}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Personal details */}
        <div className="border-t pt-6 dark:border-gray-700">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            {t("crm.newCustomer.personalDetails")}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("crm.fields.birthDate")}
              </label>
              <input
                type="date"
                value={formData.birthDate}
                onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("crm.fields.nationality")}
              </label>
              <input
                type="text"
                value={formData.nationality}
                onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                className="input"
                placeholder={t("crm.newCustomer.nationalityPlaceholder")}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("crm.fields.passportNumber")}
              </label>
              <input
                type="text"
                value={formData.passportNumber}
                onChange={(e) => setFormData({ ...formData, passportNumber: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("crm.fields.passportExpiry")}
              </label>
              <input
                type="date"
                value={formData.passportExpiry}
                onChange={(e) => setFormData({ ...formData, passportExpiry: e.target.value })}
                className="input"
              />
            </div>
          </div>
        </div>

        {/* Notes and preferences */}
        <div className="border-t pt-6 dark:border-gray-700">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            {t("crm.newCustomer.notesPreferences")}
          </h2>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("common.fields.notes")}
              </label>
              <textarea
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="input"
                placeholder={t("crm.newCustomer.notesPlaceholder")}
              />
            </div>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.newsletterSubscribed}
                onChange={(e) =>
                  setFormData({ ...formData, newsletterSubscribed: e.target.checked })
                }
                className="text-primary-600 h-4 w-4 rounded"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {t("crm.newCustomer.subscribeNewsletter")}
              </span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 border-t pt-6 dark:border-gray-700">
          <button type="button" onClick={() => navigate("/customers")} className="btn-ghost">
            {t("common.actions.cancel")}
          </button>
          <button type="submit" disabled={createMutation.isPending} className="btn-primary">
            {createMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("common.actions.saving")}
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {t("crm.newCustomer.createCustomer")}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
